/**
 * Native newsletter signup form.
 *
 * Submits template-parts/newsletter-form.php to the
 * niamito_newsletter_subscribe endpoint and reports what came back.
 *
 * The landing-page signup (landing.ts) is the ancestor of this flow, but it
 * handles exactly one outcome: on success it removes the form, and on failure
 * `if (!jsonResponse.success) {}` does nothing at all — a visitor whose signup
 * failed watches a spinner and is told nothing. Every failure is named here
 * instead: an address the browser can reject without asking the server, an
 * endpoint that answered with an error, and a request that never arrived.
 *
 * All copy comes from `niamitoNewsletter`, localised in inc/enqueue.php, so the
 * messages are translated with the rest of the theme rather than hardcoded in
 * English here.
 */

// Marks the file as a module. Without it every name below lands in the shared
// global scope the other bundled scripts occupy, where `init` already collides
// with one of them.
export {};

interface NewsletterMessages {
  invalidEmail: string;
  networkError: string;
  genericError: string;
  expired: string;
  success: string;
  alreadySigned: string;
  lang: string;
}

interface NewsletterAjax {
  ajax_url: string;
  nonce: string;
}

// Deliberately loose: a full RFC 5322 check belongs on the server, which does
// one. This only spares the visitor a round trip for an obviously wrong entry.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function messages(): NewsletterMessages {
  return ((window as any).niamitoNewsletter || {}) as NewsletterMessages;
}

function ajax(): NewsletterAjax | null {
  const data = (window as any).njamito_ajax;
  return data && data.ajax_url ? (data as NewsletterAjax) : null;
}

/**
 * Turn the endpoint's answer into a message in the reader's language.
 *
 * The endpoint returns wording too, but it is answered through admin-ajax,
 * where the catalogue that gets loaded is not always the one the page was
 * rendered with — an error can come back in English on a German page. The code
 * is language-free, so it is what gets matched; the returned text is kept only
 * as a fallback for a code this build does not know.
 */
function messageFor(
  copy: NewsletterMessages,
  code: string,
  fallback: string
): string {
  switch (code) {
    case "invalid_email":
      return copy.invalidEmail;
    case "expired":
      return copy.expired;
    case "already_subscribed":
      return copy.alreadySigned;
    case "subscribed":
      return copy.success;
    case "not_configured":
      return copy.genericError;
    default:
      return fallback || copy.genericError;
  }
}

function setLoading(form: HTMLFormElement, loading: boolean): void {
  const submit = form.querySelector(
    ".niamito-newsletter-form__submit"
  ) as HTMLButtonElement | null;

  form.classList.toggle("is-loading", loading);

  if (submit) {
    submit.classList.toggle("loading", loading);
    submit.disabled = loading;
  }
}

function showError(form: HTMLFormElement, text: string): void {
  const box = form.querySelector(
    ".niamito-newsletter-form__message"
  ) as HTMLElement | null;

  if (!box) {
    return;
  }

  box.textContent = text;
  box.hidden = false;
}

function clearError(form: HTMLFormElement): void {
  const box = form.querySelector(
    ".niamito-newsletter-form__message"
  ) as HTMLElement | null;

  if (box) {
    box.textContent = "";
    box.hidden = true;
  }
}

/**
 * Swap the form out for the success message.
 *
 * The form is hidden rather than removed so the section keeps its height and
 * the layout does not jump under the reader. Focus moves to the message
 * because the element that had focus — the submit button — is now hidden, and
 * a screen reader would otherwise be left with nothing to announce.
 */
function showSuccess(form: HTMLFormElement, text: string): void {
  const container = form.parentElement || document;
  const success = container.querySelector(
    ".product-newsletter__success-message"
  ) as HTMLElement | null;

  form.hidden = true;

  if (!success) {
    return;
  }

  if (text) {
    // The message carries a check icon as a sibling element, so the text goes
    // into its own span — writing to the container would replace the icon.
    const label = success.querySelector(
      ".product-newsletter__success-text"
    ) as HTMLElement | null;

    (label || success).textContent = text;
  }

  success.classList.add("is-visible");
  success.setAttribute("tabindex", "-1");

  // preventScroll, then scroll deliberately below: focus() alone jumps the
  // message to the edge of the viewport, which on a phone can leave it half
  // under the address bar.
  success.focus({ preventScroll: true });

  const box = success.getBoundingClientRect();
  const offscreen = box.top < 0 || box.bottom > window.innerHeight;

  if (offscreen) {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    success.scrollIntoView({
      behavior: reduced ? "auto" : "smooth",
      block: "center",
    });
  }
}

function setup(form: HTMLFormElement): void {
  form.addEventListener("submit", (event) => {
    event.preventDefault();

    const copy = messages();
    const endpoint = ajax();
    const input = form.querySelector(
      'input[name="email_address"]'
    ) as HTMLInputElement | null;

    if (!input) {
      return;
    }

    const email = input.value.trim();

    if (!EMAIL_PATTERN.test(email)) {
      showError(form, copy.invalidEmail);
      input.focus();
      return;
    }

    // Without the localised endpoint there is nothing to post to. Saying so
    // beats a submit button that stays stuck in its loading state.
    if (!endpoint) {
      showError(form, copy.genericError);
      return;
    }

    const honeypot = form.querySelector(
      'input[name="niamito_hp"]'
    ) as HTMLInputElement | null;

    clearError(form);
    setLoading(form, true);

    const data = new FormData();
    data.append("action", "niamito_newsletter_subscribe");
    data.append("email_address", email);
    data.append("nonce", endpoint.nonce);

    // admin-ajax has no page to infer a language from. WPML reads the request
    // language off this parameter, and it is what puts the right `locale:` tag
    // on the Omnisend contact — without it every signup reports as the site's
    // default language.
    if (copy.lang) {
      data.append("lang", copy.lang);
    }

    if (honeypot) {
      data.append("niamito_hp", honeypot.value);
    }

    fetch(endpoint.ajax_url, { method: "POST", body: data })
      .then((response) =>
        // A non-JSON body means a PHP fatal or an HTML error page rather than
        // an answer from the endpoint; it is reported as a generic failure.
        response.json().catch(() => null)
      )
      .then((json) => {
        setLoading(form, false);

        if (!json) {
          showError(form, copy.genericError);
          return;
        }

        if (json.success) {
          const payload = json.data || {};
          showSuccess(
            form,
            messageFor(copy, payload.code, payload.message)
          );
          return;
        }

        const error = json.data || {};
        showError(form, messageFor(copy, error.code, error.message));
      })
      .catch(() => {
        // Only a transport failure lands here: offline, DNS, a blocked
        // request. An HTTP error status resolves and is handled above.
        setLoading(form, false);
        showError(form, copy.networkError);
      });
  });
}

function init(): void {
  document
    .querySelectorAll<HTMLFormElement>("[data-niamito-newsletter]")
    .forEach(setup);
}

// The bundle is enqueued in the footer, below the section, so the form is
// normally already parsed. The readyState check covers the case where it is
// not — a deferred or moved script, or an optimisation plugin relocating it.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
